// src/components/Presentation/SlideNavigation.js
import React from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, Layout, Grid } from 'lucide-react';

const SlideNavigation = ({ 
  slides, 
  currentSlideIndex, 
  onSlideSelect, 
  onAddSlide, 
  onDeleteSlide,
  onApplyLayout 
}) => {
  const layoutTemplates = [
    { id: 'single', name: 'Single' },
    { id: 'two-horizontal', name: 'Two Horizontal' },
    { id: 'two-vertical', name: 'Two Vertical' },
    { id: 'three-column', name: 'Three Column' }
  ];

  return (
    <div className="w-80 bg-white border-r border-gray-200 flex flex-col flex-shrink-0">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900">Slides</h3>
          <button
            onClick={onAddSlide}
            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
        
        <div className="flex items-center justify-between text-sm text-gray-600">
          <button
            onClick={() => onSlideSelect(Math.max(0, currentSlideIndex - 1))}
            disabled={currentSlideIndex === 0}
            className="px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-50"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          
          <span>{currentSlideIndex + 1} / {slides?.length || 0}</span>
          
          <button
            onClick={() => onSlideSelect(Math.min(slides?.length - 1, currentSlideIndex + 1))}
            disabled={currentSlideIndex === (slides?.length - 1)}
            className="px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-50"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Slide Thumbnails */}
        <div className="p-4 space-y-3">
          {slides?.map((slide, index) => (
            <div
              key={slide.id}
              onClick={() => onSlideSelect(index)}
              className={`
                relative w-full bg-white border-2 rounded-lg cursor-pointer transition-all hover:shadow-md group
                ${index === currentSlideIndex 
                  ? 'border-indigo-500 ring-2 ring-indigo-200' 
                  : 'border-gray-200 hover:border-gray-300'
                }
              `}
            >
              <div className="aspect-[4/3] p-3">
                <div 
                  className="w-full h-full rounded border border-gray-200 flex items-center justify-center"
                  style={{ backgroundColor: slide.background?.value || '#ffffff' }}
                >
                  <span className="text-xs text-gray-500">{slide.title}</span>
                </div>
              </div>
              
              <div className="px-3 pb-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-900 truncate">
                    {index + 1}. {slide.title}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteSlide(index);
                    }}
                    className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-600 rounded"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Layout Templates */}
        <div className="border-t border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
            <Layout className="w-4 h-4 mr-2" />
            Layout Templates
          </h3>
          
          <div className="space-y-2">
            {layoutTemplates.map((template) => (
              <button
                key={template.id}
                onClick={() => onApplyLayout(template.id)}
                className="w-full p-3 border rounded-lg text-left hover:shadow-sm transition-all bg-white border-gray-200 hover:border-gray-300"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-900">{template.name}</span>
                  <Grid className="w-3 h-3 text-gray-400" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SlideNavigation;